/**
 * Database Write Logger
 * 
 * Comprehensive logging utility to track database writes for debugging
 * purposes. Logs timestamps, stores, employees, collections, documents,
 * and field changes. Can save logs to Firestore for remote viewing.
 */

import { getDefaultStore } from 'jotai'
import { Platform } from 'react-native'
import { organizationAtom } from '../global/AtomsProvider'

class DatabaseWriteLogger {
  constructor() {
    this.logs = []
    this.currentBatchId = null
    this.batchOperations = []
    this.saveToFirestore = true // Set to false to disable saving to Firestore
    this.db = null
    this.organizationId = null
  }

  /**
   * Initialize with database and organization ID for Firestore saving
   * @param {Object} db - Firestore database instance
   * @param {string} organizationId - Organization ID
   */
  initialize(db, organizationId) {
    this.db = db
    this.organizationId = organizationId
  }

  /**
   * Enable or disable saving logs to Firestore
   * @param {boolean} enable - Whether to save logs to Firestore
   */
  setSaveToFirestore(enable) {
    this.saveToFirestore = enable
  }

  /**
   * Extract call stack information to identify the caller
   * @returns {Object} Call stack information with file name and function name
   */
  getCallStackInfo() {
    try {
      const stack = new Error().stack
      if (!stack) return { callerFile: null, callerFunction: null, callStack: null }

      // Split stack into lines and filter out logger-related lines
      const stackLines = stack.split('\n').filter(line => 
        !line.includes('databaseWriteLogger') && 
        !line.includes('at Object.startFunction') &&
        !line.includes('at DatabaseWriteLogger')
      )

      // Get the first meaningful stack frame (the actual caller)
      // Format: "    at FunctionName (file:///path/to/file.js:line:column)" or
      //         "    at file:///path/to/file.js:line:column"
      const callerLine = stackLines[1] || stackLines[0] || ''
      
      let callerFile = null
      let callerFunction = null

      // Extract file path
      const fileMatch = callerLine.match(/\(([^)]+\.(js|jsx|ts|tsx)):\d+:\d+\)/) || 
                       callerLine.match(/([^/]+\.(js|jsx|ts|tsx)):\d+:\d+/)
      if (fileMatch) {
        const fullPath = fileMatch[1]
        // Extract just the filename or relative path from apps/atlas
        const pathMatch = fullPath.match(/(apps\/atlas\/.+)$/) || fullPath.match(/([^/]+\.(js|jsx|ts|tsx))$/)
        callerFile = pathMatch ? pathMatch[1] : fullPath
      }

      // Extract function name
      const functionMatch = callerLine.match(/at\s+([^\s(]+)/)
      if (functionMatch) {
        callerFunction = functionMatch[1]
        // Filter out generic names
        if (callerFunction === 'Object' || callerFunction === 'at') {
          callerFunction = null
        }
      }

      return {
        callerFile,
        callerFunction,
        callStack: stackLines.slice(0, 10).join('\n') // First 10 lines of stack
      }
    } catch (error) {
      console.error('Error extracting call stack:', error)
      return { callerFile: null, callerFunction: null, callStack: null }
    }
  }

  /**
   * Get the current platform (ios, android, web)
   * @returns {string} Platform name
   */
  getPlatform() {
    try {
      // Platform.OS returns 'ios', 'android', or 'web'
      return Platform.OS || 'unknown'
    } catch (error) {
      return 'unknown'
    }
  }

  /**
   * Check if logging to Firestore is enabled for this organization
   * @returns {boolean} True if logging is enabled
   */
  isLoggingEnabled() {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom)
      // Logging is disabled by default - only enabled if explicitly set to true
      return organization?.enableDatabaseWriteLogging === true && this.saveToFirestore
    } catch (error) {
      // If error getting organization, default to disabled (false)
      return false
    }
  }

  /**
   * Start logging a new function call
   * @param {string} functionName - Name of the function being called
   * @param {Object} context - Context information (store, employee, date range, screen, etc.)
   * @returns {string|null} Log entry ID or null if logging is disabled
   */
  startFunction(functionName, context = {}) {
    // Check if logging is enabled - if not, return null immediately (no work done)
    if (!this.isLoggingEnabled()) {
      return null
    }

    const timestamp = new Date().toISOString()
    
    // Get call stack information
    const callStackInfo = this.getCallStackInfo()
    
    // Get platform information
    const platform = this.getPlatform()

    const logEntry = {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      functionName,
      timestamp,
      context: {
        store: context.store || null,
        employeeId: context.employeeId || null,
        userId: context.userId || null,
        date: context.date ? context.date.toISOString?.() || context.date : null,
        dateRange: context.dateRange || null,
        screen: context.screen || null, // Current screen name
        platform: platform, // Platform: ios, android, or web
        callerFile: callStackInfo.callerFile || null, // File that called this function
        callerFunction: callStackInfo.callerFunction || null, // Function that called this
      },
      operations: [],
      batches: []
    }
    this.logs.push(logEntry)
    this.currentLogId = logEntry.id
    return logEntry.id
  }

  /**
   * Log a batch operation start
   * @param {string} batchId - Unique batch identifier
   * @param {string} description - Description of what the batch does
   */
  startBatch(batchId, description = '') {
    // If logging is disabled, this is a no-op
    if (!this.currentLogId) {
      return null
    }

    this.currentBatchId = batchId
    const batchLog = {
      id: batchId,
      description,
      operations: [],
      timestamp: new Date().toISOString()
    }
    
    if (this.currentLogId) {
      const currentLog = this.logs.find(log => log.id === this.currentLogId)
      if (currentLog) {
        currentLog.batches.push(batchLog)
      }
    }
    
    return batchLog
  }

  /**
   * Log a document write operation
   * @param {string} collection - Collection name
   * @param {string} docId - Document ID
   * @param {Object} fields - Fields being written
   * @param {string} operation - Operation type ('set', 'update', 'delete')
   * @param {Object} options - Additional options (merge, etc.)
   */
  logWrite(collection, docId, fields = {}, operation = 'set', options = {}) {
    // If logging is disabled, this is a no-op
    if (!this.currentLogId) {
      return null
    }

    const writeLog = {
      timestamp: new Date().toISOString(),
      collection,
      documentId: docId,
      operation,
      fields: this.sanitizeFields(fields),
      options,
      inBatch: !!this.currentBatchId,
      batchId: this.currentBatchId || null
    }

    if (this.currentBatchId && this.currentLogId) {
      const currentLog = this.logs.find(log => log.id === this.currentLogId)
      if (currentLog) {
        const batch = currentLog.batches.find(b => b.id === this.currentBatchId)
        if (batch) {
          batch.operations.push(writeLog)
        }
      }
    } else if (this.currentLogId) {
      const currentLog = this.logs.find(log => log.id === this.currentLogId)
      if (currentLog) {
        currentLog.operations.push(writeLog)
      }
    }
    
    return writeLog
  }

  /**
   * End a batch operation
   */
  endBatch() {
    // If logging is disabled, this is a no-op
    if (!this.currentLogId) {
      return
    }

    if (this.currentBatchId && this.currentLogId) {
      const currentLog = this.logs.find(log => log.id === this.currentLogId)
      if (currentLog) {
        const batch = currentLog.batches.find(b => b.id === this.currentBatchId)
        if (batch) {
          batch.endTimestamp = new Date().toISOString()
        }
      }
    }
    this.currentBatchId = null
  }

  /**
   * End function logging and output summary
   * @param {string|null} logId - Log entry ID or null if logging was disabled
   */
  endFunction(logId) {
    // If logging was disabled, logId will be null - this is a no-op
    if (!logId) {
      return
    }

    const logEntry = this.logs.find(log => log.id === logId)
    if (logEntry) {
      logEntry.endTimestamp = new Date().toISOString()
      
      // Save to Firestore if enabled (both locally and in organization settings)
      if (this.isLoggingEnabled() && this.db && this.organizationId) {
        this.saveLogToFirestore(logEntry).catch(error => {
          console.error('Error saving log to Firestore:', error)
        })
      }
    }
    this.currentLogId = null
  }

  /**
   * Save log entry to Firestore for remote viewing
   * @param {Object} logEntry - The log entry to save
   */
  async saveLogToFirestore(logEntry) {
    if (!this.db) return

    try {
      const duration = new Date(logEntry.endTimestamp) - new Date(logEntry.timestamp)
      
      // Calculate summary statistics
      const allOps = [...logEntry.operations, ...logEntry.batches.flatMap(b => b.operations)]
      const byCollection = {}
      allOps.forEach(op => {
        if (!byCollection[op.collection]) {
          byCollection[op.collection] = { total: 0, documents: new Set(), operations: {} }
        }
        byCollection[op.collection].total++
        byCollection[op.collection].documents.add(op.documentId)
        byCollection[op.collection].operations[op.operation] = (byCollection[op.collection].operations[op.operation] || 0) + 1
      })

      // Prepare the log document for Firestore
      const logDoc = {
        functionName: logEntry.functionName,
        timestamp: new Date(logEntry.timestamp),
        endTimestamp: new Date(logEntry.endTimestamp),
        duration: duration,
        context: {
          store: logEntry.context.store || null,
          employeeId: logEntry.context.employeeId || null,
          userId: logEntry.context.userId || null,
          date: logEntry.context.date || null,
          dateRange: logEntry.context.dateRange || null,
          screen: logEntry.context.screen || null, // Current screen name
          platform: logEntry.context.platform || null, // Platform: ios, android, or web
          callerFile: logEntry.context.callerFile || null, // File that called the function
          callerFunction: logEntry.context.callerFunction || null, // Function that called this
        },
        summary: {
          totalOperations: allOps.length,
          directWrites: logEntry.operations.length,
          batches: logEntry.batches.length,
          collectionsAffected: Object.keys(byCollection).length,
          collections: Object.entries(byCollection).reduce((acc, [collection, stats]) => {
            acc[collection] = {
              total: stats.total,
              uniqueDocuments: stats.documents.size,
              operations: stats.operations
            }
            return acc
          }, {})
        },
        operations: logEntry.operations.map(op => ({
          timestamp: new Date(op.timestamp),
          collection: op.collection,
          documentId: op.documentId,
          operation: op.operation,
          fields: this.sanitizeFields(op.fields || {}),
          options: this.sanitizeFields(op.options || {}),
          inBatch: op.inBatch,
          batchId: op.batchId
        })),
        batches: logEntry.batches.map(batch => ({
          id: batch.id,
          description: batch.description,
          timestamp: new Date(batch.timestamp),
          endTimestamp: batch.endTimestamp ? new Date(batch.endTimestamp) : null,
          operations: batch.operations.map(op => ({
            timestamp: new Date(op.timestamp),
            collection: op.collection,
            documentId: op.documentId,
            operation: op.operation,
            fields: this.sanitizeFields(op.fields || {}),
            options: this.sanitizeFields(op.options || {})
          }))
        })),
        organizationId: this.organizationId,
        createdAt: new Date()
      }

      // Save to organization-specific database collection
      // db is already scoped to organizations/{orgId}, so this creates organizations/{orgId}/databaseWriteLogs/{logId}
      await this.db.collection('databaseWriteLogs').add(logDoc)
    } catch (error) {
      console.error('Error saving log to Firestore:', error)
      // Don't throw - we don't want logging failures to break the main functionality
    }
  }

  /**
   * Sanitize fields to extract actual values from Firebase fieldValue operations
   * Recursively sanitizes nested objects and arrays
   */
  sanitizeFields(fields) {
    if (!fields || typeof fields !== 'object') {
      return fields
    }

    // Handle arrays
    if (Array.isArray(fields)) {
      return fields.map(item => this.sanitizeFields(item))
    }

    const sanitized = {}
    for (const [key, value] of Object.entries(fields)) {
      // Handle Firebase FieldValue.increment() and other FieldValue operations
      if (value && typeof value === 'object' && value.constructor) {
        const constructorName = value.constructor.name
        if (constructorName === 'FieldValueImpl' || constructorName === 'FieldValue') {
          // Try to extract increment/decrement value
          const valueStr = value.toString()
          if (valueStr.includes('increment')) {
            const match = valueStr.match(/increment\(([^)]+)\)/)
            sanitized[key] = `increment(${match ? match[1] : '?'})`
          } else if (valueStr.includes('delete')) {
            sanitized[key] = '<deleted>'
          } else if (valueStr.includes('serverTimestamp')) {
            sanitized[key] = '<serverTimestamp>'
          } else {
            sanitized[key] = valueStr
          }
        } else if (value.toDate && typeof value.toDate === 'function') {
          // Handle Firestore Timestamp
          sanitized[key] = value.toDate().toISOString()
        } else if (value instanceof Date) {
          // Handle Date objects
          sanitized[key] = value.toISOString()
        } else if (Array.isArray(value)) {
          // Recursively sanitize arrays
          sanitized[key] = value.map(item => this.sanitizeFields(item))
        } else if (value && typeof value === 'object') {
          // Recursively sanitize nested objects
          sanitized[key] = this.sanitizeFields(value)
        } else {
          sanitized[key] = value
        }
      } else if (value instanceof Date) {
        // Handle Date objects (outside of FieldValue checks)
        sanitized[key] = value.toISOString()
      } else {
        sanitized[key] = value
      }
    }
    return sanitized
  }


  /**
   * Get all logs
   */
  getLogs() {
    return this.logs
  }

  /**
   * Get latest log
   */
  getLatestLog() {
    return this.logs[this.logs.length - 1]
  }

  /**
   * Clear logs
   */
  clearLogs() {
    this.logs = []
    this.currentBatchId = null
    this.currentLogId = null
  }
}

// Export singleton instance
export const dbWriteLogger = new DatabaseWriteLogger()

